import React, { Component } from "react";
import axios from "axios";
import { Card, Divider, Grid, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";

const getEmptyEditor = () => ({
  _id: "",
  question: "",
  question_hindi: "",
  option1: "",
  option1_hindi: "",
  option2: "",
  option2_hindi: "",
  option3: "",
  option3_hindi: "",
  option4: "",
  option4_hindi: "",
  answer: "option1",
  description: "",
  description_hindi: "",
  question_type: "easy",
  subject_id: "",
  topic_id: "",
  level_id: "",
  question_time_seconds: 30,
  language_converted: false,
});

export default class ImportLiveTestQuestions extends Component {
  state = {
    loading: false,
    notification: false,
    message: "",
    snackbarColor: "success",
    live_test_id: "",
    live_test: {},
    questionsArray: "",
    existingQuestions: [],
    editorOpen: false,
    editingQuestion: getEmptyEditor(),
  };

  componentDidMount() {
    const id = window.location.pathname.split("/")[2];
    this.setState({ live_test_id: id }, this.getLiveTestQuestions);
  }

  showMessage = (message, snackbarColor = "success") => {
    this.setState({ notification: true, message, snackbarColor });
    setTimeout(() => {
      this.setState({ notification: false, message: "", snackbarColor: "success" });
    }, 3500);
  };

  getLiveTestQuestions = async () => {
    try {
      if (init() === "success") {
        const resp = await axios.get(
          `${Constants.getUrls.getLiveTestQuestions}${this.state.live_test_id}`
        );
        this.setState({
          live_test: resp?.data?.data?.live_test || {},
          existingQuestions: resp?.data?.data?.questions || [],
          questionsArray: "",
        });
      }
    } catch (err) {
      this.showMessage(err?.response?.data?.message || "Failed to load live test questions.", "warning");
    }
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    this.setState({ loading: true });

    try {
      const resp = await axios.post(Constants.postUrls.importLiveTestQuestions, {
        live_test_id: this.state.live_test_id,
        questionsArray: this.state.questionsArray,
      });
      this.setState({ loading: false, questionsArray: "" });
      this.showMessage(resp.data.message);
      this.getLiveTestQuestions();
    } catch (err) {
      this.setState({ loading: false });
      this.showMessage(err?.response?.data?.message || "Failed to import live test questions.", "warning");
    }
  };

  openEditor = (question) => {
    this.setState({
      editorOpen: true,
      editingQuestion: {
        ...getEmptyEditor(),
        ...question,
        question_time_seconds: question?.question_time_seconds || question?.time || 30,
      },
    });
  };

  closeEditor = () => {
    this.setState({
      editorOpen: false,
      editingQuestion: getEmptyEditor(),
    });
  };

  handleEditorInput = (e) => {
    const { name, value } = e.target;
    this.setState((prevState) => ({
      editingQuestion: {
        ...prevState.editingQuestion,
        [name]: value,
      },
    }));
  };

  handleUpdateQuestion = async () => {
    try {
      const resp = await axios.put(
        `${Constants.putUrls.updateLiveTestQuestion}${this.state.editingQuestion._id}`,
        this.state.editingQuestion
      );
      this.showMessage(resp.data.message);
      this.closeEditor();
      this.getLiveTestQuestions();
    } catch (err) {
      this.showMessage(err?.response?.data?.message || "Failed to update live test question.", "warning");
    }
  };

  handleDeleteQuestion = async (questionId) => {
    try {
      const resp = await axios.delete(`${Constants.deleteUrls.deleteLiveTestQuestion}${questionId}`);
      this.showMessage(resp.data.message);
      this.getLiveTestQuestions();
    } catch (err) {
      this.showMessage(err?.response?.data?.message || "Failed to delete live test question.", "warning");
    }
  };

  renderQuestionCard = (question, index) => {
    const options = ["option1", "option2", "option3", "option4"];

    return (
      <Card key={question._id} sx={{ p: 2.5, mb: 2, borderRadius: 3 }}>
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
          <MDBox flex={1}>
            <MDTypography variant="button" color="info" fontWeight="bold">
              Question {index + 1}
            </MDTypography>
            <MDTypography variant="h6" mt={1}>
              {question.question}
            </MDTypography>
          </MDBox>
          <MDBox display="flex" gap={1}>
            <MDButton variant="outlined" color="info" size="small" onClick={() => this.openEditor(question)}>
              Edit
            </MDButton>
            <MDButton
              variant="outlined"
              color="error"
              size="small"
              onClick={() => this.handleDeleteQuestion(question._id)}
            >
              Delete
            </MDButton>
          </MDBox>
        </MDBox>

        <Grid container spacing={1.5} mt={0.5}>
          {options.map((optionKey) => (
            <Grid item xs={12} md={6} key={optionKey}>
              <MDBox
                p={1.5}
                borderRadius="lg"
                bgColor={question.answer === optionKey ? "success" : "grey-100"}
                color={question.answer === optionKey ? "white" : "dark"}
              >
                <MDTypography variant="caption" fontWeight="bold" display="block">
                  {optionKey.toUpperCase()}
                </MDTypography>
                <MDTypography variant="button">{question[optionKey]}</MDTypography>
              </MDBox>
            </Grid>
          ))}
        </Grid>

        <MDBox mt={2}>
          <MDTypography variant="caption" fontWeight="bold" color="text">
            Correct Answer: {question.answer}
          </MDTypography>
          <MDTypography variant="caption" display="block" mt={0.75} color="text">
            Timer: {Number(question?.question_time_seconds || question?.time || 30)} seconds
          </MDTypography>
          <MDTypography variant="caption" display="block" mt={0.75} color="text">
            Explanation: {question.description || "No explanation added."}
          </MDTypography>
        </MDBox>
      </Card>
    );
  };

  renderEditor = () => {
    if (!this.state.editorOpen) {
      return null;
    }

    const { editingQuestion } = this.state;

    return (
      <Card sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <MDTypography variant="h6" mb={2}>
          Edit Live Test Question
        </MDTypography>
        <Grid container spacing={2}>
          {[
            ["question", "Question", true],
            ["question_hindi", "Question Hindi", true],
            ["option1", "Option 1"],
            ["option1_hindi", "Option 1 Hindi"],
            ["option2", "Option 2"],
            ["option2_hindi", "Option 2 Hindi"],
            ["option3", "Option 3"],
            ["option3_hindi", "Option 3 Hindi"],
            ["option4", "Option 4"],
            ["option4_hindi", "Option 4 Hindi"],
            ["description", "Description", true],
            ["description_hindi", "Description Hindi", true],
          ].map(([name, label, multiline]) => (
            <Grid item xs={12} md={name.includes("question") || name.includes("description") ? 12 : 6} key={name}>
              <InputLabel>{label}</InputLabel>
              <TextField
                multiline={Boolean(multiline)}
                minRows={multiline ? 2 : undefined}
                name={name}
                value={editingQuestion[name]}
                onChange={this.handleEditorInput}
                style={{ width: "100%" }}
              />
            </Grid>
          ))}
          <Grid item xs={12} md={4}>
            <InputLabel>Answer</InputLabel>
            <Select
              name="answer"
              value={editingQuestion.answer}
              onChange={this.handleEditorInput}
              style={{ width: "100%", height: "45px", color: "#111" }}
            >
              <MenuItem value="option1">Option 1</MenuItem>
              <MenuItem value="option2">Option 2</MenuItem>
              <MenuItem value="option3">Option 3</MenuItem>
              <MenuItem value="option4">Option 4</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} md={4}>
            <InputLabel>Difficulty</InputLabel>
            <Select
              name="question_type"
              value={editingQuestion.question_type}
              onChange={this.handleEditorInput}
              style={{ width: "100%", height: "45px", color: "#111" }}
            >
              <MenuItem value="easy">Easy</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="medium-hard">Medium Hard</MenuItem>
              <MenuItem value="hard">Hard</MenuItem>
              <MenuItem value="hardest">Hardest</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} md={4}>
            <InputLabel>Question Timer (Seconds)</InputLabel>
            <TextField
              type="number"
              name="question_time_seconds"
              value={editingQuestion.question_time_seconds}
              onChange={this.handleEditorInput}
              style={{ width: "100%" }}
            />
          </Grid>
        </Grid>

        <MDBox mt={2} display="flex" gap={1}>
          <MDButton variant="contained" color="info" size="small" onClick={this.handleUpdateQuestion}>
            Save Changes
          </MDButton>
          <MDButton variant="outlined" color="secondary" size="small" onClick={this.closeEditor}>
            Cancel
          </MDButton>
        </MDBox>
      </Card>
    );
  };

  render() {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                Import Live Test Questions
              </MDTypography>
            </MDBox>

            <MDBox py={3} px={2}>
              <MDTypography variant="button" color="text" mb={2} display="block">
                Live Test: {this.state.live_test?.title || this.state.live_test?.test_name || "-"}
              </MDTypography>

              <form onSubmit={this.handleSubmit}>
                <InputLabel>Questions JSON Array</InputLabel>
                <TextField
                  multiline
                  minRows={8}
                  name="questionsArray"
                  value={this.state.questionsArray}
                  onChange={(e) => this.setState({ questionsArray: e.target.value })}
                  style={{ width: "100%" }}
                  placeholder='[{"question":"...","option1":"...","option2":"...","option3":"...","option4":"...","answer":"option1","question_time_seconds":30}]'
                />

                <Grid align="left" mt={2}>
                  <MDButton variant="contained" size="small" color="info" type="submit">
                    {this.state.loading ? "Importing..." : "Import Questions"}
                  </MDButton>
                </Grid>
              </form>

              <Divider sx={{ my: 3 }} />
              {this.renderEditor()}
              {this.state.existingQuestions.map(this.renderQuestionCard)}
            </MDBox>

            <MDSnackbar
              color={this.state.snackbarColor}
              icon="notifications"
              title="AgriQuiz"
              content={this.state.message}
              dateTime="Just now"
              open={this.state.notification}
            />
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }
}
